/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    List,
    ListItem,
    styled,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ListItemButton } from '@mui/material';
import api from 'api';
import { Box } from '@mui/system';
import type { CountryCode } from 'types/countries';
import { marketFields } from 'utils/countries';
import { Profession } from './WorkExperienceV2';

interface JobTitleSelectorProps {
    open: boolean;
    handleClose: () => void;
    handleUpdate: (index: number, field: string, value: string | null | number) => void;
    handleRecommendedJobs: any;
    index: number;
    country: string | null;
    jobTitle: Profession | undefined;
    options: Profession[];
    countryCode: CountryCode;
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
        overflowY: 'auto',
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
        display: 'flex',
        justifyContent: 'space-between',
    },
    border: '1px solid #4a52bb',
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: 'none', // Remove individual borders
    backgroundColor: 'white', // Set background to white
    fontSize: '0.875rem', // Standard font size
    width: '100%', // Full width to fill the container
    display: 'flex',
    alignItems: 'center',
    '&.selected': {
        backgroundColor: '#f4f0ff', // Light purple background for selected
        color: '#5e35b1', // Purple text color for selected
    },
    '&:hover': {
        backgroundColor: '#f8f8f8', // Light grey on hover
    },
}));

const StyledList = styled(List)(({ theme }) => ({
    width: '100%',
    bgcolor: 'background.paper',
    overflowX: 'hidden',
    overflowY: 'auto',
    maxHeight: 300,
    border: '1px solid #ccc',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0px 2px 5px rgba(0,0,0,0.1)',
    display: 'block',
}));

const JobTitleSelector: React.FC<JobTitleSelectorProps> = ({
    open,
    handleClose,
    handleUpdate,
    index,
    country,
    jobTitle,
    options,
    handleRecommendedJobs,
    countryCode,
}) => {
    const { t } = useTranslation();
    const [description, setDescription] = useState('');
    const [suggestedTitles, setSuggestedTitles] = useState<any[]>([]);

    // const handleAutocompleteChange = (value: string | null | number) => {
    //     handleUpdate(index, 'occupation_ca_id', value);
    //     setDescription('');
    //     setSuggestedTitles([]);
    // };

    const occupationIdField = marketFields(countryCode).occupationIdKey;

    const handleListItemClick = (id: any) => {
        //const titleIndex = options.find((option) => option?.name === title)?.id || null;
        handleUpdate(index, occupationIdField, id);
        setDescription('');
    };
    const handleFetchTitles = async () => {
        if (description === '') return;
        try {
            const response = await api.job_recommendation.getRecommendedJobs(
                countryCode,
                description
            );
            // const titles = response.data.occupation_options.map((item: any) => ({id: item.id, name: item.name}))
            const titles = response.data.occupation_options.map((item: any) => ({
                id: item.id,
                name: item.name,
            }));
            setSuggestedTitles(titles);
            // handleRecommendedJobs(titles)
        } catch (e) {
            console.error(e);
            setSuggestedTitles([]);
            //  handleRecommendedJobs([])
            alert('There was some error. Please try again.');
        }
    };

    return (
        <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
        >
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {t('aiAssistant')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ overflow: 'hidden', width: '320px' }}>
                {/* {t('searchManually')}
                <Autocomplete
                    disablePortal
                    id={`profession-${country}`}
                    value={jobTitle}
                    options={options}
                    getOptionLabel={(option) => option.name || ''}
                    onChange={(_, value) => handleAutocompleteChange(value ? value?.id : null)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('classification')}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                        />
                    )}
                /> */}
                <Box>
                    {/* {t('ai_assisted_search')} */}
                    <TextField
                        label={t('jobDescription')}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        placeholder={t('jobDescriptionPlaceholder')}
                        multiline
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Box>
                <Button
                    variant="outlined"
                    onClick={handleFetchTitles}
                    fullWidth
                    sx={{
                        marginTop: 2,
                        color: '#4a52bb',
                        borderColor: '#4a52bb',
                        backgroundColor: 'white',
                        '&:hover': {
                            backgroundColor: 'white',
                            borderColor: '#4a52bb',
                        },
                        borderRadius: '3px',
                        textTransform: 'none',
                    }}
                >
                    ✨ {t('getTitles')}
                </Button>

                {suggestedTitles.length > 0 && (
                    <StyledList>
                        {suggestedTitles.map((item, index) => (
                            <ListItem
                                disablePadding
                                key={index}
                                selected={item?.name === jobTitle?.name}
                            >
                                <StyledListItemButton
                                    onClick={() => {
                                        handleListItemClick(item?.id);
                                        handleClose();
                                    }}
                                    className={item?.name === jobTitle?.name ? 'selected' : ''}
                                >
                                    {item?.name}
                                </StyledListItemButton>
                            </ListItem>
                        ))}
                    </StyledList>
                )}
            </DialogContent>
            {/* <DialogActions style={{ justifyContent: 'center' }}>
                <Button
                    onClick={() => {
                        handleFetchTitles();
                        handleClose();
                    }}
                    color="primary"
                    variant="contained" // Use 'contained' for a solid color
                    sx={{
                        width: '50%', // Set the button width to 50% of its container
                        backgroundColor: '#5e35b1', // Purple background
                        color: 'white', // White text
                        '&:hover': {
                            backgroundColor: '#502aa1', // Slightly darker purple on hover
                        },
                        borderRadius: '3px',
                        textTransform: 'none',
                        marginTop: 2, // Add margin top for spacing
                    }}>
                    ✨ {t('getTitles')}
                </Button>
            </DialogActions> */}
        </BootstrapDialog>
    );
};

export default JobTitleSelector;
